import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BulkActions = ({ selectedUsers, onBulkAction, onClose }) => {
  const [actionType, setActionType] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [subscriptionPlan, setSubscriptionPlan] = useState('');

  const actionOptions = [
    { value: '', label: 'Select Action' },
    { value: 'email', label: 'Send Email' },
    { value: 'suspend', label: 'Suspend Accounts' },
    { value: 'activate', label: 'Activate Accounts' },
    { value: 'subscription', label: 'Change Subscription' },
    { value: 'export', label: 'Export Data' }
  ];

  const subscriptionOptions = [
    { value: 'free', label: 'Free' },
    { value: 'basic', label: 'Basic' },
    { value: 'pro', label: 'Pro' },
    { value: 'enterprise', label: 'Enterprise' }
  ];

  const handleExecute = () => {
    const actionData = {
      type: actionType,
      userIds: selectedUsers,
      data: {}
    };

    if (actionType === 'email') {
      actionData.data = { subject: emailSubject, message: emailMessage };
    } else if (actionType === 'subscription') {
      actionData.data = { plan: subscriptionPlan };
    }

    onBulkAction(actionData);
    onClose();
  };

  const canExecute = () => {
    if (!actionType) return false;
    if (actionType === 'email') return emailSubject && emailMessage;
    if (actionType === 'subscription') return subscriptionPlan;
    return true;
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-modal flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Bulk Actions</h2>
            <p className="text-sm text-muted-foreground">
              {selectedUsers?.length} user{selectedUsers?.length !== 1 ? 's' : ''} selected
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <Select
            label="Action Type"
            options={actionOptions}
            value={actionType}
            onChange={setActionType}
            required
          />

          {actionType === 'email' && (
            <div className="space-y-4">
              <Input
                label="Email Subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e?.target?.value)}
                placeholder="Enter email subject"
                required
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Message
                </label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e?.target?.value)}
                  placeholder="Enter your message..."
                  rows={6}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  required
                />
              </div>
            </div>
          )}

          {actionType === 'subscription' && (
            <Select
              label="New Subscription Plan"
              options={subscriptionOptions}
              value={subscriptionPlan}
              onChange={setSubscriptionPlan}
              required
            />
          )}

          {actionType === 'suspend' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={20} className="text-red-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-red-800">Suspend Accounts</h4>
                  <p className="text-sm text-red-700 mt-1">
                    This action will suspend {selectedUsers?.length} user account{selectedUsers?.length !== 1 ? 's' : ''}. 
                    Suspended users will not be able to access the platform until their accounts are reactivated.
                  </p>
                </div>
              </div>
            </div>
          )}

          {actionType === 'activate' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-green-800">Activate Accounts</h4>
                  <p className="text-sm text-green-700 mt-1">
                    This action will activate {selectedUsers?.length} user account{selectedUsers?.length !== 1 ? 's' : ''}. 
                    Users will regain full access to the platform.
                  </p>
                </div>
              </div>
            </div>
          )}

          {actionType === 'export' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Download" size={20} className="text-blue-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-blue-800">Export User Data</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    This will export data for {selectedUsers?.length} selected user{selectedUsers?.length !== 1 ? 's' : ''} 
                    in CSV format including profile information, subscription details, and activity history.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleExecute}
            disabled={!canExecute()}
            variant={actionType === 'suspend' ? 'destructive' : 'default'}
          >
            {actionType === 'suspend' ? 'Suspend Users' :
             actionType === 'activate' ? 'Activate Users' :
             actionType === 'email' ? 'Send Email' :
             actionType === 'subscription' ? 'Update Subscriptions' :
             actionType === 'export'? 'Export Data' : 'Execute Action'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkActions;